'use client';

import { getUniqueInventoryImageUrl } from '@/tools/inventory';
import { IInventory, InventoryField } from '@lamesarv-sdk/types';
import { useEffect, useRef } from 'react';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';
import { ImageActions } from './ImageActions';

interface IPdpImageDefaultProps {
  item: IInventory;
}

export const PdpImageDefault = ({ item }: IPdpImageDefaultProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // On lg+, size the container so the image height (following its ratio) matches the PdpInfo height
  useEffect(() => {
    const container = containerRef.current;
    const image = imageRef.current;
    const info = document.querySelector<HTMLElement>('.pdp-info');

    if (!container || !image || !info) return;

    const mediaQuery = window.matchMedia('(min-width: 1200px)');

    const updateWidth = () => {
      if (!mediaQuery.matches) {
        container.style.width = '';
        container.style.flexShrink = '';
        return;
      }

      const ratio = image.naturalHeight ? image.naturalWidth / image.naturalHeight : 0;

      if (!ratio || info.offsetHeight <= 0) return;

      const parent = container.parentElement;
      const parentStyles = parent ? getComputedStyle(parent) : null;
      const maxWidth = parent
        ? parent.clientWidth -
          parseFloat(parentStyles!.paddingLeft) -
          parseFloat(parentStyles!.paddingRight) -
          info.offsetWidth -
          (parseFloat(parentStyles!.columnGap) || 0)
        : Infinity;

      container.style.width = `${Math.round(Math.min(info.offsetHeight * ratio, maxWidth))}px`;
      container.style.flexShrink = '0';
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);

    observer.observe(info);

    if (container.parentElement) observer.observe(container.parentElement);

    image.addEventListener('load', updateWidth);
    mediaQuery.addEventListener('change', updateWidth);
    window.addEventListener('resize', updateWidth);

    return () => {
      observer.disconnect();
      image.removeEventListener('load', updateWidth);
      mediaQuery.removeEventListener('change', updateWidth);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  return (
    <div ref={containerRef} data-testid="pdp-gallery" className="pdp-default-image relative flex">
      <img
        ref={imageRef}
        alt={`${item[InventoryField.isSold] ? '(Sold)' : ''} ${item[InventoryField.title]}`.trim()}
        src={getUniqueInventoryImageUrl(item)}
        className="bg-black"
      />
      {!item[InventoryField.isSold] && <ImageActions item={item} />}
      {item[InventoryField.isSold] && (
        <div
          className="
              absolute lg:-top-2 lg:-left-2 h-[116px] w-[116px] overflow-hidden pointer-events-none
              before:content-[] lg:before:content-[''] before:absolute before:top-0 before:right-0 before:border-4 before:border-solid before:border-[#942115] before:border-t-transparent before:border-l-transparent
              after:content-[] lg:after:content-[''] after:absolute after:bottom-0 after:left-0 after:border-4 after:border-solid after:border-[#942115] after:border-t-transparent after:border-l-transparent"
        >
          <span className="absolute top-[29px] -left-[44px] flex h-[34px] w-[180px] -rotate-45 items-center justify-center bg-[#C63926] text-xl sm:text-2xl lg:text-[1.75rem] font-bold text-white">
            SOLD
          </span>
        </div>
      )}
    </div>
  );
};
